import React, { useEffect, useState, useRef } from "react";

import formatTime from "../../../utils/formatTime";
import maybeIcon from "../../../assets/Images/mainImage.png";
import axios from "axios";
import env from "../../../config/env";
import ContentRenderer from "./ContentRenderer";
import renderAssistantMessage from "../utils/renderAssistantMessage";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ImageWithFallback from "../../../components/ImageFallback";
import Icon from "../../YourAIAgents/components/components/icon";
import { CircularProgress, Tooltip } from "@mui/material";
import { useUsersContext } from "../../../context/usersContext";
import { useUserInfo } from "../../../context/user";
import { useNavigate, useParams } from "react-router-dom";
import { Add, AddCommentOutlined, InfoOutlined } from "@mui/icons-material";
import PermissionRequiredModal from "./PermissionRequiredModal";
import UserMessage from "./ConversationSections/UserMessage";
import AIMessage from "./ConversationSections/AIMessage";
import RotatingWaitMessage from "./RotatingWaitMessage";

const Conversation = ({
  agencyInfo,
  submitNewMessage,
  UploadOnKB,
  IsStreamApi,
  selectedFiles,
}) => {
  const params = useParams();
  const {
    fetchMessages,
    resumePendingChat,
    messages,
    isUserChating,
    agentPlaceholderText,
    setOpenShareModal,
    chatError,
    setChatError,
  } = useUsersContext();
  const [userInfo] = useUserInfo();
  const [loading, setLoading] = useState(false);
  const [openPermissionModal, setOpenPermissionModal] = useState(false);
  const navigate = useNavigate();

  const handleErrorButtonClick = () => {
    if (
      chatError?.showError &&
      (chatError?.errorMessage?.includes("couldn't find this conversation") || chatError?.errorMessage?.includes("This agent has been paused or removed"))
    ) {
      navigate("/app/chat/");
      setChatError({
        showError: false,
        errorMessage: "",
        classNames: "",
        iconColor: "",
      });
    } else if (
      chatError?.showError &&
      chatError?.errorMessage?.includes("agent set up for this yet")
    ) {
      if (userInfo?.role !== "user") {
        navigate("/app/your-ai-agents/");
        setChatError({
          showError: false,
          errorMessage: "",
          classNames: "",
          iconColor: "",
        });
      } else {
        setOpenPermissionModal(true);
      }
    }
  };

  const bottomRef = useRef(null);
  const [copied, setCopied] = useState(null);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);

  const getMessages = async () => {
    setLoading(true);
    const data = await fetchMessages(params.id);
    setLoading(false);
    resumePendingChat?.(params.id, data);
  };

  useEffect(() => {
    if (params.id) getMessages();
  }, [params.id]);

  const copyToClipboard = (content, id) => {
    const htmlContent = renderAssistantMessage(content);

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    document.body.appendChild(tempDiv);

    const range = document.createRange();
    range.selectNodeContents(tempDiv);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    try {
      document.execCommand("copy");
      setCopied(id);
    } catch (err) {
      console.error("Failed to copy content: ", err);
    }

    document.body.removeChild(tempDiv);
  };

  const handleOpenPromptModal = (content) => {
    navigate("/app/chat/tasks/new", {
      state: { autoloadfirsttext: content || "" },
    });
  };

  const handleCreateTable = () =>
    submitNewMessage(
      "Convert this response to the table format",
      "notsavetodb",
      "notimage",
      () => {},
      false,
    );

  const handleTryAgain = () =>
    submitNewMessage(
      "Rewrite the response",
      "notsavetodb",
      "notimage",
      () => {},
      false,
    );

  const handleContinue = () =>
    submitNewMessage(
      "Continue",
      "notsavetodb",
      "notimage",
      () => {},
      false,
    );

  const textToSpeak = (text, messageId) => {
    if (!text?.trim()) return;
    if (speechSynthesis.speaking) speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.onend = () => setSpeakingMessageId(null);
    speechSynthesis.speak(utterance);
    setSpeakingMessageId(messageId);
  };

  const stopSpeech = () => {
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
      setSpeakingMessageId(null);
    }
  };

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ block: "end" });
    }
  }, [messages]);

  if (loading)
    return (
      <div className="w-full h-full flex flex-col gap-3 items-center justify-center">
        <CircularProgress size={40} />
        <p className="text-[12px]">Loading Messages, please wait...</p>
      </div>
    );

  return (
    <div className="conversation__wrapper mx-auto w-full relative overflow-y-auto overflow-x-hidden v3chat__content px-6">
      <div
        className="flex items-center gap-2 absolute top-1 left-0 w-fit cursor-pointer py-2"
        onClick={() => setOpenShareModal(params.id)}
      >
        <Icon id="share" style={{ fontSize: "12px" }} />
        <p className="text-[#303030] font-[400] text-[12px]">Share</p>
      </div>

      <div className="text-center relative mt-2.5 mb-6">
        <span className="bg-[#e1f2fa] inline-block text-[#000000] text-[0.75rem] py-1.5 px-2.5 rounded-[5px]">
          {`Welcome ${userInfo?.userName || ""}`}
        </span>
      </div>

      <div className="flex flex-col w-full">
        {messages?.map((message, msgIndex) => {
          const htmlContent = renderAssistantMessage(message.content);
          return (
            <>
              {message.image ? (
                <div
                  className={`p-2 mb-3 font-400 text-[0.85rem] text-black w-fit max-w-[95%] leading-[20px] rounded-[5px] relative flex ${message.sender
                    ? "inline-flex max-w-full min-w-full px-7 py-2.5 items-center content-center gap-2.5 flex-wrap"
                    : "inline-flex px-5 py-4 items-center content-center gap-2.5 flex-wrap rounded-[25px] border !border-[#e5e5e5] bg-[#f9f9f9] self-end text-black font-[600] text-justify"
                    }`}
                ></div>
              ) : message.sender ? (
                <div className="mb-5 text-[14px] text-black w-fit max-w-[95%] leading-[1.65] rounded-[5px] relative inline-flex max-w-full min-w-full px-7 py-4 items-start content-start gap-2.5 flex-wrap">
                  <div className="flex gap-4 w-full">
                    <div className="w-[55px] flex-shrink-0">
                      <img
                        className="rounded-[38px] mx-auto border !border-gray px-1 py-1.5 bg-white w-[50px] h-[50px]"
                        src={
                          agencyInfo?.Customization?.chatapp_logo
                            ? agencyInfo?.Customization?.chatapp_logo
                            : maybeIcon
                        }
                        width={50}
                        alt="maybe icon"
                      />
                    </div>
                    <div className="mt-1 w-full">
                      <ContentRenderer
                        index={msgIndex}
                        htmlContent={htmlContent}
                        conversation_id={params?.id}
                        isLatestMessage={
                          msgIndex === messages.length - 1 &&
                          message.status !== "read"
                        }
                        IsStreamApi={IsStreamApi}
                      />

                      <div
                        style={{ width: "100%" }}
                        className="absolute bottom-0 flex justify-between "
                      >
                        <div className="w-50 flex justify-start outline-none">
                          <button
                            onClick={() =>
                              copyToClipboard(message.content, msgIndex)
                            }
                            aria-label="Copy"
                            style={{ marginRight: "10px" }}
                          >
                            <Tooltip
                              title={
                                agencyInfo?.Customization?.copyicon_text &&
                                  copied === msgIndex
                                  ? "Copied"
                                  : "Copy"
                              }
                              placement="top"
                            >
                              <span>
                                <Icon id="new_copy" />
                              </span>
                            </Tooltip>
                          </button>

                          <button
                            aria-label="Create Table"
                            onClick={() =>
                              submitNewMessage(
                                "Convert this response to the table format",
                                "notsavetodb",
                                "notimage",
                                () => { },
                                false,
                              )
                            }
                            style={{ marginRight: "10px" }}
                          >
                            <Tooltip
                              title={
                                agencyInfo?.Customization?.table_text
                                  ? agencyInfo?.Customization?.table_text
                                  : "Create Table"
                              }
                              placement="top"
                            >
                              <span>
                                <Icon id="new_table" />
                              </span>
                            </Tooltip>
                          </button>
                          <button
                            className="hovercolor"
                            style={{ marginRight: "10px" }}
                            onClick={() =>
                              submitNewMessage(
                                "Rewrite the response",
                                "notsavetodb",
                                "notimage",
                                () => { },
                                false,
                              )
                            }
                          >
                            <Tooltip
                              title={
                                agencyInfo?.Customization?.tryagain_text
                                  ? agencyInfo?.Customization?.tryagain_text
                                  : "Try Again"
                              }
                              placement="top"
                            >
                              <span>
                                <Icon id="v3_refresh" />
                              </span>
                            </Tooltip>
                          </button>
                          <button
                            className="hovercolo"
                            onClick={() =>
                              submitNewMessage(
                                "Continue",
                                "notsavetodb",
                                "notimage",
                                () => { },
                                false,
                              )
                            }
                          >
                            <Tooltip
                              title={
                                agencyInfo?.Customization?.continue_text
                                  ? agencyInfo?.Customization?.continue_text
                                  : "Continue"
                              }
                              placement="top"
                            >
                              <span>
                                <Icon id="v3_continue" />
                              </span>
                            </Tooltip>
                          </button>

                          <button
                            aria-label="download"
                            onClick={() => {
                              // submitslide(message, msgIndex); // Pass the collected messages to the function
                            }}
                            style={{ marginLeft: "10px" }}
                          >
                            {speakingMessageId === msgIndex ? (
                              <div
                                style={{
                                  position: "relative",
                                  color: "#757575",
                                }}
                                onClick={stopSpeech}
                                className="hover-text"
                              >
                                <Tooltip title="Stop" placement="top">
                                  <span>
                                    <Icon id="smallChatContinuous" />
                                  </span>
                                </Tooltip>
                              </div>
                            ) : (
                              <div
                                style={{ position: "relative" }}
                                onClick={() =>
                                  textToSpeak(message.content, msgIndex)
                                }
                                className="hover-text"
                              >
                                <Tooltip title="Listen" placement="top">
                                  <span>
                                    <Icon id="speaker_icon" />
                                  </span>
                                </Tooltip>
                              </div>
                            )}
                          </button>
                        </div>

                        <div className="flex items-center gap-1 pb-1 text-[14px] text-[#757575] mr-[130px]">
                          <Icon
                            id="magicstar"
                            style={{ marginRight: "5px", fontSize: "12px" }}
                          />
                          {message.assistant_name?.includes(
                            "Agent builder for profile",
                          )
                            ? "Agent Builder"
                            : message.assistant_name}{" "}
                          {message?.plugin_used > 0 &&
                            message?.plugin_used
                              ?.slice(0, 3)
                              ?.map((plugin, index) => (
                                <ImageWithFallback
                                  key={index}
                                  src={`https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/${plugin?.brand
                                    ?.toLowerCase()
                                    ?.replaceAll(" ", "")}.svg`}
                                  alt={plugin?.brand}
                                />
                              ))}{" "}
                          {message?.plugin_used > 0 &&
                            message?.plugin_used > 3 && (
                              <span>
                                + {message?.plugin_used?.length - 3} more
                              </span>
                            )}
                        </div>
                      </div>
                      <span className="flex items-center text-[rgba(0,0,0,0.45)] mr-1.5 text-[0.7rem] font-[500]">
                        {formatTime(message.time)}
                      </span>
                      <span className="w-[65px] inline-block h-[3px] bg-transparent mt-3"></span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative pt-5 pb-2.5 px-2.5 w-full">
                  <div style={{ textAlign: "right" }}>
                    <div
                      className="chat__msg chat__msg--sent relative min-w-[300px]"
                    // ref={assignRef()}
                    >
                      <span
                        id={`id_${msgIndex}`}
                        className="!font-[400] text-[14px]"
                      >
                        {message.content}
                      </span>
                      <span className="chat__msg-filler"> </span>

                      <span className="chat__msg-footer">
                        <span> {formatTime(message.time)} </span>
                        <Icon
                          id={
                            message?.status === "sent"
                              ? "singleTick"
                              : "dobuleTickWhite"
                          }
                          aria-label={message?.status}
                          className={`chat__msg-status-icon ${message?.status === "read"
                            ? "chat__msg-status-icon--blue"
                            : ""
                            }`}
                        />
                      </span>
                      <div className="iconWrapper justify-center items-center gap-2.5 flex">
                        <button
                          onClick={() =>
                            copyToClipboard(message.content, msgIndex)
                          }
                          aria-label="Copy message"
                          className="mr-2.5 flex items-center text-[#757575] text-[12px]"
                        >
                          <div className="relative">
                            <div class="hover-text ml-1">
                              {agencyInfo?.Customization?.copyicon_text ? (
                                <Tooltip
                                  title={
                                    agencyInfo?.Customization?.copyicon_text
                                      ? agencyInfo?.Customization?.copyicon_text
                                      : "Copy"
                                  }
                                  placement="top"
                                >
                                  <span>
                                    <Icon id="new_copy" />
                                  </span>
                                </Tooltip>
                              ) : (
                                <Icon id="new_copy" />
                              )}
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>

                    <span className="hover-text cursor-pointer relative left-2">
                      <Tooltip title="Create a Task" placement="top">
                        <span
                          onClick={() => handleOpenPromptModal(message.content)}
                        >
                          <Icon id={"v3_4dots"} />
                        </span>
                      </Tooltip>
                    </span>

                    <div
                      className="my-5 mx-0"
                      style={{ margin: "20px 0" }}
                    ></div>

                    <div className="flex gap-2 justify-end max-w-[60%] ml-auto">
                      {message?.file_ids?.slice(0, 3)?.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            marginLeft: "auto",
                            marginBottom: "21px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <div className="file_upload_fileId">
                            <span style={{ marginRight: "10px" }}>
                              <Icon id="file" />
                            </span>
                            {item?.name?.split("")?.length > 10
                              ? `${item?.name
                                .split("/")
                              [
                                item?.name.split("/").length - 1
                              ]?.substring(0, 10)}...`
                              : item?.name}
                          </div>

                          {!(
                            message?.file_type === "KB" ||
                            item.name.includes(".png") ||
                            item.name.includes(".jpg")
                          ) && (
                              <span
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  cursor: "pointer",
                                }}
                                onClick={() => UploadOnKB(item)}
                              >
                                <AddCircleOutlineIcon sx={{ color: "#757575" }} />
                              </span>
                            )}
                        </div>
                      ))}
                      {message?.file_ids?.length - 3 > 0 && (
                        <span className="whitespace-nowrap mt-4">
                          +{message?.file_ids.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })}

        <div className="relative mt-[20px] ml-2">
          {isUserChating &&
            (selectedFiles?.length > 0 ? (
              <div className="flex items-center gap-1 text-[#757575] text-[14px]">
                <Icon
                  id="aichatIconInput"
                  style={{ marginRight: "5px", fontSize: "12px" }}
                />
                Analysing files
              </div>
            ) : (
              <RotatingWaitMessage fallbackText={agentPlaceholderText} />
            ))}
        </div>

        <div id="errorofmaxfile" />

        {chatError?.showError && chatError?.errorMessage !== "" && (
          <div
            className={`w-[97%] rounded-[25px] m-auto mt-4 ${
              chatError?.classNames || "border !border-[#c00f0c] bg-[#fff7f6]"
            }`}
          >
            <div className="flex items-center justify-between w-full px-[30px] py-[22px]">
              <p className="py-2.5 px-0 m-0 text-[14px] flex">
                <span className="pr-4">
                  <InfoOutlined
                    sx={{ color: chatError?.iconColor || "#C00F0C" }}
                  />
                </span>
                <span>{chatError?.errorMessage}</span>
              </p>
              {(chatError?.errorMessage?.includes(
                "couldn't find this conversation",
              ) ||
                chatError?.errorMessage?.includes(
                  "agent set up for this yet",
                )) || chatError?.errorMessage?.includes('This agent has been paused or removed') && (
                  <button
                    onClick={handleErrorButtonClick}
                    className="px-[22px] py-2.5 text-[14px] bg-white border !border-[#E5EAF1] rounded-[5px]"
                  >
                    <span className="pr-3">
                      {chatError?.errorMessage?.includes(
                        "agent set up for this yet",
                      ) ? (
                        <Add />
                      ) : (
                        <AddCommentOutlined
                          sx={{ transform: "rotateY(180deg)", color: "#000" }}
                        />
                      )}
                    </span>
                    <span>
                      {chatError?.errorMessage?.includes(
                        "agent set up for this yet",
                      )
                        ? "Build a custom Agent"
                        : "Start New Chat"}
                    </span>
                  </button>
                )}
            </div>
          </div>
        )}
      </div>

      {openPermissionModal && (
        <PermissionRequiredModal
          isOpen={openPermissionModal}
          onClose={() => setOpenPermissionModal(false)}
        />
      )}

      <div ref={bottomRef} id="gotovie" />
    </div>
  );
};

export default Conversation;
